import cross from '@assets/nuevos-iconos/x.png'
import { MODE_3D_VIEW, MODES_2D_VIEWER } from '@constants'
import { saveProyect } from '@proxies/saveProyect'
import * as Dialog from '@radix-ui/react-dialog'
import * as Form from '@radix-ui/react-form'
import { useAppStore } from '@store'
import { Item, Types, Zone } from '@types'
import { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { OBB } from 'three/examples/jsm/math/OBB'
import { useTrackEvent } from '../../hooks/useAnalytics'
import { emptyClientInfo } from '../../store/baseValues'
import { usePreciosHubContext } from '../../context/PreciosHubContext'

export default function SaveProject() {
  const setMode = useAppStore.use.setMode()
  const storeClientInfo = useAppStore((store) => store.clientInfo)
  const t = useAppStore.use.t()
  const items = useAppStore((store) => store.items)
  const areas = useAppStore((store) => store.areas)
  const totalPrice = useAppStore((store) => store.price)
  const objectsScene = useAppStore((store) => store.layers[store.currentLayer].items)
  const sceneBoundingBoxes: Map<string, { type: Types; boundingBox: OBB }> = useAppStore(
    (store) => store.sceneBoundingBoxes,
  )
  const savedViews = useAppStore((store) => store.savedViews)
  const currLayer = useAppStore((store) => store.currentLayer)
  const mode = useAppStore.use.mode()
  const navigate = useNavigate()
  const sceneGLB = useAppStore((store) => store.sceneGLB)
  const openModal = useAppStore.use.openModal()
  const closeModal = useAppStore.use.closeModal()
  const isModalOpen = useAppStore((state) => state.isModalOpen)
  const location = useLocation()
  const error = useLocation()
  const openProject = useAppStore.use.openProyect()
  const collisionableItems = useAppStore((store) => store.collisionableItems)
  const { trackProjectSave, trackEvent } = useTrackEvent()
  const { resultadoPrecios, isConnected } = usePreciosHubContext()
  const [clientInfo, setClientInfo] = useState(emptyClientInfo)
  const initialMode = useRef(mode)
  const isSavingRef = useRef(false)
  const [image, setImage] = useState('')
  const setIsSaving = useAppStore.use.setIsSaving()
  const isInSaveProject = useRef(false)
  const savedSelectionRef = useRef<{
    currentObjectID: string
    selected: Set<string>
    currentLayer: number
    hideDimentions3D: boolean
  } | null>(null)

  // Guardar el estado de las cotas de forma persistente para restaurarlo después de guardar
  const savedHideDimentions3DRef = useRef<boolean | null>(null)

  // Efecto para abrir/cerrar el modal según la ruta
  useEffect(() => {
    if (location.pathname === '/planner/saveProject') {
      isInSaveProject.current = true
      // Guardar modo inicial
      const store = useAppStore.getState()
      const currentMode = store.mode
      initialMode.current = currentMode

      // IMPORTANTE: Guardar el estado de las cotas y selección ANTES de abrir el modal
      // Esto asegura que guardamos el estado que el usuario tenía antes de abrir el modal
      const currentObjectID = store.currentObjectID
      const currentLayer = store.currentLayer
      const currentSelected = new Set(store.layers[currentLayer].selected)
      const originalHideDimentions3D =
        store.hideDimentions3D === undefined || store.hideDimentions3D === null
          ? false
          : store.hideDimentions3D

      savedSelectionRef.current = {
        currentObjectID,
        selected: currentSelected,
        currentLayer,
        hideDimentions3D: originalHideDimentions3D,
      }
      savedHideDimentions3DRef.current = originalHideDimentions3D

      openModal()

      if (currentMode !== MODE_3D_VIEW) {
        setMode(MODE_3D_VIEW)
      }

      setImage('')
    } else {
      isInSaveProject.current = false
      closeModal()
    }

    return () => {
      // Restaurar modo cuando se sale de la ruta
      if (isInSaveProject.current && initialMode.current !== MODE_3D_VIEW) {
        setMode(initialMode.current)
      }
    }
  }, [location.pathname, openModal, closeModal, setMode])

  // Efecto adicional para asegurar que el modal esté abierto cuando estamos en la ruta
  useEffect(() => {
    if (location.pathname === '/planner/saveProject' && !isModalOpen) {
      openModal()
    }
  }, [location.pathname, isModalOpen, openModal])

  // Función helper para restaurar la selección y estado de cotas
  const restoreSelection = () => {
    useAppStore.setState((state) => {
      // Restaurar cotas primero (prioridad al ref persistente)
      if (savedHideDimentions3DRef.current !== null) {
        state.hideDimentions3D = savedHideDimentions3DRef.current
      } else if (savedSelectionRef.current?.hideDimentions3D !== undefined) {
        state.hideDimentions3D = savedSelectionRef.current.hideDimentions3D
      }

      // Restaurar selección si existe
      if (savedSelectionRef.current) {
        const { currentObjectID, selected, currentLayer } = savedSelectionRef.current
        state.currentObjectID = currentObjectID
        // Usar el layer guardado para restaurar en el layer correcto
        if (state.layers[currentLayer]) {
          state.layers[currentLayer].selected.clear()
          selected.forEach((id) => {
            state.layers[currentLayer].selected.add(id)
          })
        }
      }
    })
  }

  // Función para restaurar solo después de guardar exitosamente
  const restoreAfterSave = () => {
    // Restaurar selección si existe
    if (savedSelectionRef.current) {
      const { currentObjectID, selected, currentLayer } = savedSelectionRef.current
      useAppStore.setState((state) => {
        state.currentObjectID = currentObjectID
        if (state.layers[currentLayer]) {
          state.layers[currentLayer].selected.clear()
          selected.forEach((id) => {
            state.layers[currentLayer].selected.add(id)
          })
        }
      })
    }

    // Restaurar cotas del estado guardado
    if (savedHideDimentions3DRef.current !== null) {
      useAppStore.setState((state) => {
        state.hideDimentions3D = savedHideDimentions3DRef.current!
      })
      savedHideDimentions3DRef.current = null // Limpiar después de restaurar
    }
  }

  // Efecto para capturar imagen sin gizmo cuando se abre el modal
  useEffect(() => {
    if (location.pathname === '/planner/saveProject' && !image) {
      let isCapturing = true

      const captureImageWithoutGizmo = async () => {
        // Esperar un poco para que el modo 3D se establezca
        await new Promise((resolve) => setTimeout(resolve, 200))

        // Deseleccionar temporalmente para ocultar el gizmo y las cotas en la imagen
        useAppStore.setState((state) => {
          state.currentObjectID = ''
          state.layers[state.currentLayer].selected.clear()
          state.hideDimentions3D = true // Ocultar cotas temporalmente
        })

        // Esperar varios frames para que se renderice sin el gizmo ni las cotas
        await new Promise((resolve) => requestAnimationFrame(resolve))
        await new Promise((resolve) => requestAnimationFrame(resolve))
        await new Promise((resolve) => setTimeout(resolve, 150))

        const canvas = document.querySelector('canvas')
        const imageUrl = canvas?.toDataURL() || ''

        if (isCapturing) {
          setImage(imageUrl)

          // Esperar un frame antes de restaurar para asegurar que la imagen se guardó
          await new Promise((resolve) => requestAnimationFrame(resolve))

          // Restaurar el estado
          restoreSelection()

          // Forzar una actualización adicional después de un pequeño delay
          await new Promise((resolve) => setTimeout(resolve, 50))

          // Verificar y forzar la restauración nuevamente si es necesario
          const storeCheck = useAppStore.getState()
          if (
            savedHideDimentions3DRef.current !== null &&
            storeCheck.hideDimentions3D !== savedHideDimentions3DRef.current
          ) {
            useAppStore.setState((state) => {
              state.hideDimentions3D = savedHideDimentions3DRef.current!
            })
          }

          if (savedSelectionRef.current) {
            const { currentObjectID, selected, currentLayer } = savedSelectionRef.current
            const currentSelected = Array.from(storeCheck.layers[storeCheck.currentLayer].selected)
            const expectedSelected = Array.from(selected)

            if (
              storeCheck.currentObjectID !== currentObjectID ||
              currentSelected.length !== expectedSelected.length ||
              !expectedSelected.every((id) => currentSelected.includes(id))
            ) {
              useAppStore.setState((state) => {
                state.currentObjectID = currentObjectID
                if (state.layers[currentLayer]) {
                  state.layers[currentLayer].selected.clear()
                  selected.forEach((id) => {
                    state.layers[currentLayer].selected.add(id)
                  })
                }
              })
            }
          }
        }
      }

      captureImageWithoutGizmo()

      // Cleanup: restaurar selección si el componente se desmonta antes de completar
      return () => {
        isCapturing = false
        restoreSelection()
      }
    }
  }, [location.pathname, image])

  useEffect(() => {
    setClientInfo({
      nombreProyecto: storeClientInfo.nombreProyecto,
      descripcion: storeClientInfo.descripcion,
      nombre: storeClientInfo.nombre,
      apellidos: storeClientInfo.apellidos,
      direccion: storeClientInfo.direccion,
      codPostal: storeClientInfo.codPostal,
      poblacion: storeClientInfo.poblacion,
      email: storeClientInfo.email,
      pais: storeClientInfo.pais,
      movil: storeClientInfo.movil,
      idDisenos: storeClientInfo.idDisenos,
      idVersiones: storeClientInfo.idVersiones,
      idImpuestos: storeClientInfo.idImpuestos ?? '',
    })
  }, [storeClientInfo])

  function handleInput(event, isProjectName = false) {
    setClientInfo((prevState) => {
      let value = event.target.value

      // Filtrar caracteres especiales no permitidos para el nombre del proyecto
      if (isProjectName) {
        // Caracteres no permitidos: + / \ % & * ( ) @ # ! ^ ? [ ] { } " ; < > = | , :
        const forbiddenChars = /[+\/\\%&*()@#!^?[\]{}";<>=|,:]/g
        value = value.replace(forbiddenChars, '')
      }

      if (isProjectName) {
        return {
          ...prevState,
          idDisenos: null,
          idVersiones: null,
          [event.target.name]: value,
        }
      }
      return {
        ...prevState,
        [event.target.name]: value,
      }
    })
  }

  async function sendToAPI() {
    if (isSavingRef.current) return

    isSavingRef.current = true

    // Restaurar selección antes de guardar
    restoreSelection()

    try {
      const store = useAppStore.getState()
      if (store.restoreAllLights) {
        store.restoreAllLights()
        await new Promise((resolve) => setTimeout(resolve, 100))
      }

      // Usar la imagen que ya está en el estado (capturada sin gizmo)
      const data1 = { nombre: 'Vista actual', imageUrl: image || '' }

      const visuals = [data1]

      const updatedItems = useAppStore.getState().items
      const updatedLayers = useAppStore.getState().layers
      const lastView = useAppStore.getState().lastView

      // Track save attempt
      trackEvent('save_attempt', 'project_action', 'project_save_form_submit')

      const billableUnitsCount = Array.from(updatedItems.values()).filter((sceneItem: any) =>
        sceneItem?.type === 'linkedItem' || sceneItem?.type === 'items' || sceneItem?.type === 'walls',
      ).length
      const precioAGuardar =
        billableUnitsCount <= 0
          ? 0
          : isConnected && resultadoPrecios?.precioTotal !== undefined
            ? resultadoPrecios.precioTotal
            : 0

      const result = await saveProyect(
        clientInfo,
        objetos,
        precioAGuardar,
        date,
        visuals,
        updatedLayers,
        currLayer,
        updatedItems,
        areas,
        sceneBoundingBoxes,
        sceneGLB,
        collisionableItems,
        savedViews || [],
        lastView,
      )

      if (result.success) {
        // Track successful save
        trackProjectSave()
        trackEvent('save_success', 'project_action', 'project_saved_successfully', totalPrice)
        setIsSaving(true)

        // Verificar si hay una acción pendiente después de guardar
        // IMPORTANTE: Verificar ANTES de llamar a handleClose() porque handleClose() limpia localStorage
        const pendingAction = localStorage.getItem('pendingAction')
        const pendingFrom = localStorage.getItem('pendingFrom')

        if (pendingAction === 'newDesignAfterSave' && pendingFrom === 'newDesign') {
          // Limpiar la acción pendiente ANTES de cerrar
          localStorage.removeItem('pendingAction')
          localStorage.removeItem('pendingFrom')
          sessionStorage.removeItem('pendingSave')

          handleClose()

          // Esperar un poco antes de crear el nuevo diseño
          setTimeout(() => {
            // Crear nuevo diseño en blanco
            const resetStore = useAppStore.getState().resetStore
            const unselect = useAppStore.getState().unselectAll
            const setMode = useAppStore.getState().setMode

            // Navegar primero para evitar que openProject() interfiera
            navigate('/planner')

            // Resetear el store y crear el nuevo diseño (sin paredes)
            resetStore()
            setTimeout(() => {
              // Ya no creamos paredes automáticamente - solo grid y luces
              unselect()
              setMode('MODE_IDLE')
            }, 100)
          }, 3000) // Esperar 3 segundos para asegurar que la imagen se haya guardado
        } else if (pendingAction === 'openProjectAfterSave' && pendingFrom === 'openProject') {
          // Obtener el idVers del proyecto a abrir
          const pendingProjectIdVers = localStorage.getItem('pendingProjectIdVers')

          // Limpiar la acción pendiente ANTES de cerrar
          localStorage.removeItem('pendingAction')
          localStorage.removeItem('pendingFrom')
          localStorage.removeItem('pendingProjectIdVers')
          sessionStorage.removeItem('pendingSave')

          handleClose()

          // Esperar un poco antes de abrir el proyecto
          setTimeout(async () => {
            if (pendingProjectIdVers) {
              const setStoreFromBack = useAppStore.getState().setStoreFromBack
              const setMode = useAppStore.getState().setMode
              const setOBBS = useAppStore.getState().setOBBScene
              const openModal = useAppStore.getState().openModal
              const setHideDimentions3D = useAppStore.getState().setHideDimentions3D
              const idVers = Number(pendingProjectIdVers)

              openModal()
              navigate('/planner/downloading')
              await setStoreFromBack(idVers)
              setTimeout(() => {
                // Activar cotas al abrir proyecto
                setHideDimentions3D(false)
                setMode('MODE_IDLE')
                navigate('/planner')
                setOBBS()
              }, 2000)
            }
          }, 3000) // Esperar 3 segundos para asegurar que la imagen se haya guardado
        } else {
          handleClose()
          // Restaurar selección y cotas después de guardar exitosamente
          restoreAfterSave()
          // Esperar un poco antes de abrir el proyecto para dar tiempo a que la imagen se guarde
          // El guardado de la imagen ocurre de forma asíncrona con un delay de 2 segundos
          setTimeout(() => {
            openProject()
            // Asegurarse de restaurar el estado después de abrir el proyecto también
            restoreAfterSave()
          }, 3000) // Esperar 3 segundos para asegurar que la imagen se haya guardado
        }
      } else {
        trackEvent('save_error', 'project_action', result.message || 'unknown_error')
        navigate('/planner/saveProject', { state: { error: result.message } })
      }
    } catch (error: any) {
      trackEvent('save_error', 'project_action', error.message || 'unknown_error')
      if (error.message && error.message.includes('Unexpected end of JSON input')) {
        navigate('/planner/login')
      } else {
        navigate('/planner/saveProject', { state: { error: error.message || 'Unknown error' } })
      }
    } finally {
      isSavingRef.current = false
    }
  }

  useEffect(() => {
    if (error?.state?.error === 'Unexpected end of JSON input') {
      navigate('/planner/login')
    }
  }, [error, navigate])

  const formfield = 'mt-1 flex justify-between'

  const objetos: { idUnidades: number; zonas: { nombre: string; idTexturas: number }[] }[] =
    Array.from(objectsScene)
      .map((id) => {
        const currItem = items.get(id) as Item
        let zones: { nombre: string; idTexturas: number }[]
        if (currItem?.textures?.length > 0) {
          zones = currItem?.textures.map((val: Zone) => ({
            nombre: val.name,
            idTexturas: val.id === '' ? 1 : (val.id ?? 1),
          }))
        } else {
          zones = [{ nombre: 'null', idTexturas: 1 }]
        }
        return { idUnidades: currItem?.idUnidades, zonas: zones }
      })
      .filter((currItem) => currItem.idUnidades !== undefined)

  function validateAndSubmit() {
    const regex = /^[a-zA-ZÁÉÍÓÚáéíóúÜüñÑ0-9 _-]*$/
    if (!regex.test(clientInfo.nombreProyecto)) {
      console.error('El nombre del proyecto contiene caracteres no permitidos.')
      return
    }
    sendToAPI()
  }

  const date = new Date().toISOString()
  const currentDate = new Date()
  const formattedDate = `${currentDate.getDate()}/${currentDate.getMonth() + 1}/${currentDate.getFullYear()}`

  const handleClose = () => {
    // Restaurar selección antes de cerrar
    restoreSelection()

    sessionStorage.removeItem('pendingSave')
    localStorage.removeItem('pendingAction')
    localStorage.removeItem('pendingFrom')
    closeModal()
    navigate('/planner')
    setMode(initialMode.current)
  }

  return (
    <Dialog.Root
      open={isModalOpen}
      onOpenChange={(open) => {
        if (!open && location.pathname === '/planner/saveProject') {
          // Si el modal se cierra y estamos en la ruta, restaurar selección y cerrar
          handleClose()
        }
      }}
    >
      <Dialog.Portal>
        <div className='white-background save-project-overlay h-100vh w-100vw scrollbar absolute bottom-0 left-0 right-0 top-0 m-auto flex flex-col items-center overflow-x-hidden overflow-y-scroll'>
          <Dialog.Content
            onInteractOutside={(e) => {
              e.preventDefault()
              e.stopPropagation()
            }}
            className='border-colorAtemporal absolute inset-0 left-1/2 top-1/2 z-20 h-[490px] w-[1125px] -translate-x-1/2 -translate-y-1/2 place-items-center justify-center border-[1px] border-solid bg-C/Color1'
          >
            <Form.Root>
              <div className='ml-6 mt-6 flex h-[500px] w-[440px] flex-col space-y-3'>
                <Dialog.Title className='text-xl text-C/Color5'>
                  <span
                    className='border-colorAtemporal inline-block border-b-2 pb-1'
                    style={{ width: '200px' }}
                  >
                    {'Guardar proyecto'}
                  </span>
                </Dialog.Title>
                <Dialog.Description className='sr-only'>P</Dialog.Description>
                {error?.state?.error && (
                  <div className='absolute left-[14rem] top-[6.5rem] text-xs text-C/Color6'>
                    {error.state?.error}
                  </div>
                )}
                <button
                  type='button'
                  className='absolute right-2.5 top-0.5 cursor-pointer'
                  onClick={() => {
                    // Restaurar selección antes de cerrar
                    restoreSelection()

                    if (!MODES_2D_VIEWER.includes(initialMode.current)) {
                      setMode(initialMode.current)
                    }
                    sessionStorage.removeItem('pendingSave')
                    localStorage.removeItem('pendingAction')
                    localStorage.removeItem('pendingFrom')
                    closeModal()
                    navigate('/planner')
                  }}
                >
                  <img src={cross} />
                </button>
                <Form.Field
                  name='proyectname'
                  className={formfield}
                >
                  <Form.Label className='mt-3 text-lg text-C/Color5'>{'Nombre proyecto'} </Form.Label>
                  <Form.Control asChild>
                    <input
                      onChange={(e) => handleInput(e, true)}
                      onKeyDown={(e) => {
                        // Prevenir que se escriban caracteres especiales no permitidos
                        const forbiddenChars = [
                          '+',
                          '/',
                          '\\',
                          '%',
                          '&',
                          '*',
                          '(',
                          ')',
                          '@',
                          '#',
                          '!',
                          '^',
                          '?',
                          '[',
                          ']',
                          '{',
                          '}',
                          '"',
                          ';',
                          '<',
                          '>',
                          '=',
                          '|',
                          ',',
                          ':',
                        ]
                        if (forbiddenChars.includes(e.key)) {
                          e.preventDefault()
                          return false
                        }

                        if (e.key === 'Enter') {
                          e.preventDefault()
                          validateAndSubmit()
                        }
                      }}
                      onPaste={(e) => {
                        e.preventDefault()
                        const pastedText = e.clipboardData.getData('text')
                        const forbiddenChars = /[+\/\\%&*()@#!^?[\]{}";<>=|,:]/g
                        const cleanedText = pastedText.replace(forbiddenChars, '')

                        const syntheticEvent = {
                          target: {
                            name: 'nombreProyecto',
                            value: cleanedText,
                          },
                        }
                        handleInput(syntheticEvent as any, true)
                      }}
                      type='text'
                      name='nombreProyecto'
                      id='proyectName'
                      value={clientInfo.nombreProyecto}
                      title='Solo se permiten letras, números, espacios, guiones bajos y guiones medios.'
                      className='proyectName text-md ml-1 mt-3 w-[250px] border border-solid border-C/Color4 pl-2 text-C/Color4'
                    />
                  </Form.Control>
                </Form.Field>
                <Form.Field name='description'>
                  <div className='flex justify-center'>
                    <img
                      src={image}
                      className='mb-3 mt-1 h-[280px] object-contain'
                      alt='image project'
                    />
                  </div>
                </Form.Field>
              </div>
              <div className='-mt-[505px] ml-[480px] flex flex-col'>
                <div className='mb-1 ml-6 mt-1 w-[600px]'>
                  <h5 className='mb-4 text-lg text-C/Color5'>
                    <span
                      className='border-colorAtemporal inline-block border-b-2 pb-1'
                      style={{ width: '200px' }}
                    >
                      {'Datos del cliente'}
                    </span>
                  </h5>
                  <div className='mt-4 grid grid-cols-2 gap-x-8 gap-y-3 text-left text-lg'>
                    <Form.Field
                      name='username'
                      className='flex justify-between'
                    >
                      <Form.Label className='text-lg text-C/Color5'>{'Nombre'}</Form.Label>
                      <Form.Control asChild>
                        <input
                          className='text-md h-7 w-[190px] border border-solid border-C/Color4 pl-1 text-C/Color4'
                          onChange={(e) => handleInput(e)}
                          type='text'
                          name='nombre'
                          id='userName'
                          value={clientInfo.nombre}
                        />
                      </Form.Control>
                    </Form.Field>
                    <Form.Field
                      name='usersurname'
                      className='flex justify-between'
                    >
                      <Form.Label className='text-lg text-C/Color5'>{'Apellidos'}</Form.Label>
                      <Form.Control asChild>
                        <input
                          className='text-md h-7 w-[190px] border border-solid border-C/Color4 pl-1 text-C/Color4'
                          onChange={(e) => handleInput(e)}
                          type='text'
                          name='apellidos'
                          id='userSurname'
                          value={clientInfo.apellidos}
                        />
                      </Form.Control>
                    </Form.Field>
                    <Form.Field
                      name='address'
                      className='flex justify-between'
                    >
                      <Form.Label className='text-lg text-C/Color5'>{'Dirección'}</Form.Label>
                      <Form.Control asChild>
                        <input
                          className='text-md h-7 w-[190px] border border-solid border-C/Color4 pl-1 text-C/Color4'
                          onChange={(e) => handleInput(e)}
                          type='text'
                          name='direccion'
                          id='userAddress'
                          value={clientInfo.direccion}
                        />
                      </Form.Control>
                    </Form.Field>
                    <Form.Field
                      name='city'
                      className='flex justify-between'
                    >
                      <Form.Label className='text-lg text-C/Color5'>{'Ciudad'}</Form.Label>
                      <Form.Control asChild>
                        <input
                          className='text-md h-7 w-[190px] border border-solid border-C/Color4 pl-1 text-C/Color4'
                          onChange={(e) => handleInput(e)}
                          type='text'
                          name='poblacion'
                          id='userCity'
                          value={clientInfo.poblacion}
                        />
                      </Form.Control>
                    </Form.Field>
                    <Form.Field
                      name='postcode'
                      className='flex justify-between'
                    >
                      <Form.Label className='text-lg text-C/Color5'>{'C.P.'}</Form.Label>
                      <Form.Control asChild>
                        <input
                          className='text-md h-7 w-[110px] border border-solid border-C/Color4 pl-1 text-C/Color4'
                          onChange={(e) => handleInput(e)}
                          type='text'
                          name='codPostal'
                          id='userPostcode'
                          value={clientInfo.codPostal}
                        />
                      </Form.Control>
                    </Form.Field>
                    <Form.Field
                      name='country'
                      className='flex justify-between'
                    >
                      <Form.Label className='text-lg text-C/Color5'>{'Pais'}</Form.Label>
                      <Form.Control asChild>
                        <input
                          className='text-md h-7 w-[190px] border border-solid border-C/Color4 pl-1 text-C/Color4'
                          onChange={(e) => handleInput(e)}
                          type='text'
                          name='pais'
                          id='userCountry'
                          value={clientInfo.pais}
                        />
                      </Form.Control>
                    </Form.Field>
                    <Form.Field
                      name='email'
                      className='flex justify-between'
                    >
                      <Form.Label className='text-lg text-C/Color5'>{'Email'}</Form.Label>
                      <Form.Control asChild>
                        <input
                          className='text-md h-7 w-[190px] border border-solid border-C/Color4 pl-1 text-C/Color4'
                          onChange={(e) => handleInput(e)}
                          type='email'
                          name='email'
                          id='userEmail'
                          value={clientInfo.email}
                        />
                      </Form.Control>
                    </Form.Field>
                    <Form.Field
                      name='number'
                      className='flex justify-between'
                    >
                      <Form.Label className='text-lg text-C/Color5'>{'Móvil'}</Form.Label>
                      <Form.Control asChild>
                        <input
                          className='text-md h-7 w-[190px] border border-solid border-C/Color4 pl-1 text-C/Color4'
                          onChange={(e) => handleInput(e)}
                          type='number'
                          name='movil'
                          id='userPhone'
                          value={clientInfo.movil}
                        />
                      </Form.Control>
                    </Form.Field>
                  </div>
                </div>
                <div className='ml-6 mt-6 flex w-[600px] flex-col'>
                  <Form.Field name='description'>
                    <div className='mb-4 flex flex-col'>
                      <Form.Label className='mb-2 text-lg text-C/Color5'>{'Descripción'}</Form.Label>
                      <Form.Control asChild>
                        <textarea
                          onChange={(e) => handleInput(e)}
                          name='descripcion'
                          id='proyectDescription'
                          value={clientInfo.descripcion}
                          className='h-[80px] w-full border border-solid border-C/Color4 pl-1 text-C/Color4'
                        />
                      </Form.Control>
                    </div>
                  </Form.Field>
                  <div className='mb-4'>
                    <p className='text-lg text-C/Color5'>
                      {'Fecha de inicio'}: {formattedDate}
                    </p>
                  </div>
                  <div className='flex justify-end'>
                    <Dialog.Close asChild>
                      <button
                        className='bg-colorAtemporal mr-2 mt-2 h-[40px] w-[200px] text-xl text-C/Color1'
                        type='button'
                        onClick={sendToAPI}
                      >
                        {'Guardar'}
                      </button>
                    </Dialog.Close>
                  </div>
                </div>
              </div>
            </Form.Root>
          </Dialog.Content>
        </div>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
